import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { PageProps } from '@customTypes/page'
import { GetOrderPurchasesType, OrderPurchaseType } from '@customTypes/orderPurchase'

import QueryKeys from '@constants/queryKeys'

import { useParams } from '@hooks/params'
import { useIsMobile, useTableMinWidth } from '@hooks/mobile'
import { ErrorService } from '@utils/error'
import DataRepo from '@api/datasource/data'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useListState } from '@mantine/hooks'
import queryClient from '@api/datasource/query'
import { notifications } from '@mantine/notifications'
import { onFilterOrderPurchase } from '@utils/orderPurchase'
import { isLoadingMutation, isLoadingOrRefetchQuery } from '@utils/network'
import { Button, List, Paper, Text, Title } from '@mantine/core'
import { RoutesApp } from '@constants/routes'
import { IconCubePlus, IconExternalLink } from '@tabler/icons-react'
import TableCustom, { GenericColumnType } from '@components/shared/table'
import moment from 'moment'
import { DATE_FORMAT, UI } from '@constants/app'
import ConfirmationModal from '@components/shared/confirmationModal'
import NumberFormat from '@components/shared/Number'
import Cont from '@components/shared/cont'

const OrderPurchase = (props: PageProps) => {
  const navigate = useNavigate()

  const isMobile = useIsMobile()

  const minWidth = useTableMinWidth(isMobile)

  const { cardPreview } = props

  const [modalDelete, setModalDelete] = React.useState<OrderPurchaseType[] | null>(null)

  const [params, setParams] = useParams<GetOrderPurchasesType>({
    queryKey: QueryKeys.GET_ORDERS_PURCHASES_KEY,
    initialParams: {},
  })

  const [selectedRows, handlers] = useListState<string>([])

  const ordersPurchasesQuery = useQuery<OrderPurchaseType[], ErrorService, OrderPurchaseType[]>({
    queryKey: [QueryKeys.GET_ORDERS_PURCHASES_KEY],
    queryFn: async () => {
      const response = await DataRepo.documentEntitiesService.getOrderPurchases(params)

      return response
    },
  })

  const ordersPurchasesDeleteMutation = useMutation<void, ErrorService, string[]>({
    mutationFn: async (uids) => {
      const response = await DataRepo.documentEntitiesService.deleteOrderPurchases(uids)

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_ORDERS_PURCHASES_KEY],
        refetchType: 'all',
        exact: true,
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al eliminar la orden de compra',
        })
      }

      setModalDelete(null)
      handlers.setState([])

      return notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Orden(es) de compra eliminada(s) correctamente',
      })
    },
  })

  const memoFiltersOrdersPurchases = React.useMemo(
    () =>
      onFilterOrderPurchase({
        data: ordersPurchasesQuery.data,
        params,
        limit: cardPreview ? 10 : undefined,
      }),
    [ordersPurchasesQuery.data, params, cardPreview],
  )

  React.useEffect(() => {
    return () => {
      handlers.setState([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoading = isLoadingOrRefetchQuery(ordersPurchasesQuery)

  if (cardPreview) {
    return (
      <React.Fragment>
        <Paper
          withBorder
          className="cd-flex cd-flex-col cd-gap-y-[1rem]"
          p="lg"
          radius="md"
          shadow="md"
        >
          <Link
            className="hover:cd-text-blue-400 cd-cursor-pointer cd-w-fit cd-flex cd-flex-row cd-gap-x-[0.5rem] cd-items-center"
            to={RoutesApp.ORDER_PURCHASES}
          >
            <Title order={3}>Ordenes de compra</Title>
            <IconExternalLink size={20} />
          </Link>
          <TableCustom<OrderPurchaseType>
            columns={buildColumns()}
            data={memoFiltersOrdersPurchases}
            hidePagination={cardPreview}
            keyId="uid"
            loading={isLoading}
            loadingMessage="Cargando ordenes de compra"
            minWidth={minWidth}
            noDataMessage="No se encontraron ordenes de compra"
            onDetail={({ uid }) => {
              navigate(RoutesApp.ORDER_PURCHASES_VIEW.replace(':uid', uid))
            }}
          />
        </Paper>
      </React.Fragment>
    )
  }

  return (
    <Cont>
      <div className="cd-flex cd-flex-col cd-gap-y-[2rem]">
        <div className="cd-flex cd-flex-col md:cd-flex-row cd-gap-[1rem] cd-grow">
          <Text className="cd-text-lg md:cd-basis-[80%]">Ordenes de compra</Text>
          <Button
            fullWidth
            className="md:cd-basis-[20%]"
            color="green"
            leftSection={<IconCubePlus size={18} />}
            size={UI.Size}
            variant="filled"
            onClick={() => {
              navigate(RoutesApp.ORDER_PURCHASES_FORM.replace('/:uid', ''))
            }}
          >
            Crear
          </Button>
        </div>

        <TableCustom<OrderPurchaseType>
          allData={ordersPurchasesQuery.data}
          columns={buildColumns()}
          data={memoFiltersOrdersPurchases}
          defaultColumnSearch="name"
          keyId="uid"
          loading={isLoading}
          loadingMessage="Cargando ordenes de compra"
          minWidth={minWidth}
          noDataMessage="No se encontraron ordenes de compra"
          selectedRows={selectedRows}
          onChangeSelected={handlers}
          onDelete={(ids) => {
            const data = ordersPurchasesQuery.data?.filter((item) => ids.includes(item.uid))

            if (!data?.length) return

            setModalDelete(data)
          }}
          onDetail={({ uid }) => {
            navigate(RoutesApp.ORDER_PURCHASES_VIEW.replace(':uid', uid))
          }}
          onSearch={(query, field, startDate, endDate) =>
            setParams({ query, field, startDate, endDate })
          }
        />
      </div>

      <ConfirmationModal
        cancelColor="gray"
        cancelText="Cancelar"
        confirmColor="red"
        confirmText="Eliminar"
        loading={isLoadingMutation(ordersPurchasesDeleteMutation)}
        open={Boolean(modalDelete?.length)}
        title="Eliminar orden de compra"
        onCancel={() => setModalDelete(null)}
        onConfirm={() => {
          if (!modalDelete) return

          ordersPurchasesDeleteMutation.mutate(modalDelete.map((item) => item.uid))
        }}
      >
        {modalDelete?.length === 1 && (
          <Text className="cd-text-base">
            ¿Estás seguro que deseas eliminar la orden de compra{' '}
            <Text inherit className="cd-text-base cd-font-bold" component="span">
              {modalDelete[0].sequential} - {modalDelete[0].name}
            </Text>
            ?
          </Text>
        )}
        {modalDelete && modalDelete?.length > 1 && (
          <React.Fragment>
            <Text className="cd-text-base">
              ¿Estás seguro que deseas eliminar las siguientes ordenes de compra?
            </Text>
            <List withPadding className="cd-mt-[0.5rem] cd-list-disc" type="unordered">
              {modalDelete.map((item) => (
                <List.Item key={item.uid}>
                  <Text className="cd-text-base">
                    {item.sequential} - {item.name}
                  </Text>
                </List.Item>
              ))}
            </List>
          </React.Fragment>
        )}
      </ConfirmationModal>
    </Cont>
  )

  function buildColumns() {
    const columns: GenericColumnType<OrderPurchaseType>[] = [
      {
        key: 'sequential',
        label: 'Nro. Orden',
        align: 'right',
        width: '10%',
        type: 'text',
        searchable: true,
      },
      {
        key: 'name',
        label: 'Nombre',
        align: 'left',
        width: '25%',
        type: 'text',
        searchable: true,
      },
      {
        key: 'description',
        label: 'Descripción',
        align: 'left',
        width: '35%',
        type: 'text',
        searchable: true,
      },
      {
        key: 'rawMaterials',
        label: 'Materiales',
        align: 'right',
        width: '10%',
        type: 'calc',
        render: ({ rawMaterials }) => (
          <NumberFormat skipRole decimals={0} prefix="" value={rawMaterials.length} />
        ),
      },
      {
        key: 'date',
        label: 'Fecha',
        width: '15%',
        type: 'calc',
        searchable: true,
        render: ({ createdAt }) => moment.unix(createdAt).format(DATE_FORMAT),
      },
    ]

    return columns
  }
}

export default OrderPurchase
